// hooks
import { useMemo } from 'react';
import useCounterColumns from './columns/useCounterColumns';
import useDayColumns from './columns/useDayColumns';
import useIdentityColumns from './columns/useIdentityColumns';

// types
import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import type { Period } from 'components/shared/index';
import type { QualificationResponse } from 'RestAPIs/roster/qualifications/types';
import type { CostCentersFromOthersPrefValue } from './useGetColumns.types';
import type { EmployeeNameFormatTypes } from '../RosterPlan.types';

/**
 * The columns of both roster grids, in three groups: what identifies the row, one column per day of
 * the displayed period, and the counters pinned to the right.
 *
 * One hook serves both grids — `isOpenRequests` picks the open requests variant of each group —
 * because the two have to stay column for column comparable to line up on screen.
 */
export const useGetColumns = ({
  datesToDisplay,
  period = 'month',
  isOpenRequests = false,
  qualificationsByCostCenters = [],
  showSourceCostCenter,
  showCommitmentCostCenter,
  costCenterFromOthersPrefValue,
  rosterPlanColumnsVisibility,
  employeeNameFormat,
  showSortOrderColumn = false
}: {
  datesToDisplay: string[];
  period: Period | null;
  isOpenRequests?: boolean;
  qualificationsByCostCenters?: QualificationResponse[];
  showSourceCostCenter?: boolean;
  showCommitmentCostCenter?: boolean;
  costCenterFromOthersPrefValue?: CostCentersFromOthersPrefValue;
  rosterPlanColumnsVisibility?: { [key: string]: boolean };
  employeeNameFormat?: EmployeeNameFormatTypes;
  showSortOrderColumn?: boolean;
}) => {
  const identityColumns = useIdentityColumns({
    isOpenRequests,
    showSortOrderColumn,
    showSourceCostCenter,
    showCommitmentCostCenter,
    employeeNameFormat
  });

  const dayColumns = useDayColumns({
    datesToDisplay,
    period,
    isOpenRequests,
    qualificationsByCostCenters,
    costCenterFromOthersPrefValue
  });

  const counterColumns = useCounterColumns({
    period,
    isOpenRequests,
    rosterPlanColumnsVisibility
  });

  return useMemo<GridColDef<GridValidRowModel>[]>(
    () => [...identityColumns, ...dayColumns, ...counterColumns],
    [identityColumns, dayColumns, counterColumns]
  );
};
